/* eslint-disable no-restricted-imports */
import { useForm } from '@/utils/useForm';
import { formatPhoneNumber, formatPhoneNumberValue } from '@/utils/InputPhone';
import { Grid, Stack, Typography } from '@mui/material';
import { ChangeEvent, FC, FocusEvent, useMemo, useState } from 'react';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REG_EX } from '@/utils/address';
import { CheckboxProps } from '@mui/material/Checkbox';
import { DpCheckbox } from '@/components/blocks/DpCheckbox';
import {
	AccountBlockedEmailSX,
	AccountInformationFormTitleSX,
} from '@/components/content/Account/styles/accountInformationForm';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { useDpValeLoginPhone } from '@/data/Content/Dpvale';
import { ModalAccountLoginPhoneOtp } from '@/components/blocks/ModalAccountLoginPhoneOtp';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { useNotifications } from '@/data/Content/Notifications';
import { DpvaleUser } from 'integration/generated/transactions/data-contracts';

interface AccountInformationFormProps {
	personInfo: DpvaleUser;
	isLoading?: boolean;
	onMutatePersonInfo: () => void;
}

const initialValuesError = {
	phone: false,
};

export const BpCheckbox = (props: CheckboxProps) => (
	<DpCheckbox
		sx={{
			'&:hover': { bgcolor: 'transparent' },
		}}
		disableRipple
		color="default"
		inputProps={{ 'aria-label': 'Checkbox demo' }}
		{...props}
	/>
);

export const AccountInformationForm: FC<AccountInformationFormProps> = ({
	personInfo,
	isLoading,
	onMutatePersonInfo,
}) => {
	const initialForm = useMemo(
		() => ({
			...personInfo,
			otp: '',
			loadingResendCode: false,
			loadingLoginPhone: false,
			showOtp: false,
		}),
		[personInfo]
	);

	const { values, handleSubmit, formRef, handleInputChange, onNamedValueChange } =
		useForm(initialForm);
	const { loginPhoneSendOtp, loginPhone } = useDpValeLoginPhone();
	const { showSuccessMessage } = useNotifications();

	const [error, setError] = useState(initialValuesError);

	const formatDateToDisplay = (date: string): string => {
		if (!date) return '';
		const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		return match ? `${match[3]}/${match[2]}/${match[1]}` : date;
	};

	const formattedDateOfBirth = useMemo(
		() => formatDateToDisplay(values.birthdate ?? ''),
		[values.birthdate]
	);

	const handleFormSubmit = async () => {
		const cleanedPhoneNumber = values.phone.replace(/\D/g, '');

		if (personInfo.phone === cleanedPhoneNumber) {
			return;
		}

		if (!cleanedPhoneNumber) {
			setError((prev) => ({
				...prev,
				phone: true,
			}));
			return;
		} else if (cleanedPhoneNumber.length !== 10) {
			setError((prev) => ({
				...prev,
				phone: true,
			}));
			return;
		}

		const res = await loginPhoneSendOtp({ phone: cleanedPhoneNumber });

		if (res?.sent) {
			onNamedValueChange('otp', EMPTY_STRING);
			onNamedValueChange('showOtp', true);
		}
	};

	const handleResendCode = async () => {
		try {
			onNamedValueChange('loadingResendCode', true);
			const cleanedPhoneNumber = values.phone.replace(/\D/g, '');
			const res = await loginPhoneSendOtp({ phone: cleanedPhoneNumber });
			if (res?.sent) {
				onNamedValueChange('otp', EMPTY_STRING);
			}
		} finally {
			onNamedValueChange('loadingResendCode', false);
		}
	};

	const handleLoginPhone = async () => {
		try {
			onNamedValueChange('loadingLoginPhone', true);
			const cleanedPhoneNumber = values.phone.replace(/\D/g, '');
			const res = await loginPhone({ phone: cleanedPhoneNumber, code: values.otp });
			if (res?.success) {
				await onMutatePersonInfo();
				showSuccessMessage('Teléfono actualizado correctamente');
				onNamedValueChange('showOtp', false);
			}
		} finally {
			onNamedValueChange('loadingLoginPhone', false);
		}
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		const { name } = e.target;
		const key = name as keyof DpvaleUser;
		if (!values[key]) {
			setError((prev) => ({
				...prev,
				[name]: true,
			}));
		}
	};

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name } = e.target;
		handleInputChange(e);
		setError((prev) => ({
			...prev,
			[name]: false,
		}));
	};

	const handleChangeOtp = (otp: string[]) => {
		handleInputChange({ target: { name: 'otp', value: otp.join('') } } as any);
	};

	const handleCloseOtp = () => {
		onNamedValueChange('showOtp', false);
	};

	const helperText = useMemo(
		() => ({
			phone: error.phone
				? values.phone.length === 0
					? 'Captura tu Teléfono'
					: 'Teléfono inválido'
				: EMPTY_STRING,
		}),
		[error, values.phone]
	);

	return (
		<>
			<Stack component="form" noValidate ref={formRef} onSubmit={handleSubmit(handleFormSubmit)}>
				<Typography component={'h1'} sx={AccountInformationFormTitleSX}>
					{'INFORMACIÓN PERSONAL'}
				</Typography>

				<Grid container spacing={2} marginBottom={2}>
					<Grid item xs={12}>
						<DpOutlinedInput
							id="address-form-email"
							data-testid="address-form-email"
							name="email"
							label="Correo electrónico"
							value={values.email}
							disabled={true}
							fullWidth
							sx={AccountBlockedEmailSX}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={2} marginBottom={2}>
					<Grid item xs={12} sm={6}>
						<DpOutlinedInput
							id="address-form-firstName"
							data-testid="address-form-firstName"
							name="firstName"
							label="Nombre"
							value={values.firstName}
							fullWidth
							disabled={true}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DpOutlinedInput
							id="address-form-lastName"
							data-testid="address-form-lastName"
							name="lastName"
							label="Apellido paterno"
							value={values.lastName}
							disabled={true}
							fullWidth
						/>
					</Grid>
				</Grid>
				<Grid container spacing={2} marginBottom={2}>
					<Grid item xs={12} sm={6}>
						<DpOutlinedInput
							id="address-form-lastNameMat"
							data-testid="address-form-lastNameMat"
							name="secondLastName"
							label="Apellido materno"
							value={values.secondLastName}
							disabled={true}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DpOutlinedInput
							required
							id="address-form-phone"
							name="phone"
							type="tel"
							data-testid="address-form-phone"
							label="Teléfono"
							value={formatPhoneNumberValue(values.phone)}
							error={error.phone}
							helperText={helperText.phone}
							inputProps={{ maxLength: 12, pattern: REG_EX.PHONE.source }}
							fullWidth
							autoComplete="tel"
							onBlur={handleBlur}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								formatPhoneNumber(event, onChange)
							}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={2} marginBottom={2}>
					<Grid item xs={12} sm={6}>
						<DpOutlinedInput
							id="address-form-dateOfBirth"
							name="dateOfBirth"
							data-testid="address-form-dateOfBirth"
							label="Fecha de nacimiento"
							value={formattedDateOfBirth}
							disabled={true}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DpOutlinedInput
							id="address-form-gender"
							name="gender"
							data-testid="address-form-gender"
							label="Género"
							disabled={true}
							value={values.gender}
							fullWidth
						/>
					</Grid>
				</Grid>
				<DpButton
					type="submit"
					variant="primary"
					fullWidth
					disabled={values.loadingLoginPhone || isLoading}
				>
					{'Actualizar información personal'}
				</DpButton>
			</Stack>
			{values.showOtp ? (
				<ModalAccountLoginPhoneOtp
					otp={values.otp}
					isLoading={values.loadingResendCode || values.loadingLoginPhone}
					onResendCode={handleResendCode}
					onChange={handleChangeOtp}
					onSubmit={handleLoginPhone}
					onClose={handleCloseOtp}
				/>
			) : null}
		</>
	);
};
